/**
 * Generate Flow Page — 5-step wizard
 *   1. Pick Template
 *   2. Pick Client
 *   3. Pick Letterhead
 *   4. Chat — confirm/collect variables
 *   5. Preview & Export
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FileText, Sparkles, Layers, Users, ChevronRight, ArrowLeft, Check, Loader2,
  Pencil, Send, Download, Plus, MessageSquare, X, Edit, Search,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PageHeader } from '@/components/shared/PageHeader';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import apiClient from '@/services/api/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import SendEmailDialog from './SendEmailDialog';

// ==================== Types ====================

type ResolverSource = 'client' | 'xero' | 'system' | 'documents' | 'registry' | 'user_input' | 'unresolved';

interface ResolvedVariable {
  name: string;
  value: string;
  source: ResolverSource;
  sourceDetail?: string;
  isUserPrompted?: boolean;
  confidence?: number;
  error?: string;
  searchable?: boolean;
}

interface Template {
  id: string;
  name: string;
  category?: string;
  description?: string;
  variables?: Array<{ name: string; isUserPrompted?: boolean; resolverSource?: string }>;
}

interface Client {
  id: string;
  displayName?: string;
  display_name?: string;
  name?: string;
  abn?: string;
  clientType?: string;
  entity_type?: string;
  email?: string;
}

interface Letterhead {
  id: string;
  name: string;
  isDefault?: boolean;
}

interface ChatMessage {
  id: string;
  role: 'assistant' | 'user' | 'system';
  content: string;
  variableName?: string;
  citations?: Array<{ documentName?: string; excerpt?: string; sourceUrl?: string }>;
}

const SOURCE_COLORS: Record<ResolverSource, string> = {
  client: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
  xero: 'bg-green-500/10 text-green-600 border-green-500/30',
  system: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
  documents: 'bg-sky-500/10 text-sky-600 border-sky-500/30',
  registry: 'bg-indigo-500/10 text-indigo-600 border-indigo-500/30',
  user_input: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  unresolved: 'bg-slate-500/10 text-slate-600 border-slate-500/30',
};

const SOURCE_LABELS: Record<ResolverSource, string> = {
  client: 'Client',
  xero: 'Xero',
  system: 'System',
  documents: 'AI · docs',
  registry: 'Learned',
  user_input: 'User input',
  unresolved: 'Missing',
};

// ==================== Page ====================

export default function GenerateFlowPage() {
  const { templateId: urlTemplateId } = useParams<{ templateId?: string }>();
  const navigate = useNavigate();

  const [step, setStep] = useState<number>(urlTemplateId ? 2 : 1);
  const [templateId, setTemplateId] = useState<string | undefined>(urlTemplateId);
  const [clientId, setClientId] = useState<string | undefined>();
  const [letterheadId, setLetterheadId] = useState<string | undefined>();
  const [variableValues, setVariableValues] = useState<Record<string, string>>({});
  const [variableProvenance, setVariableProvenance] = useState<Record<string, ResolverSource>>({});
  const [resolved, setResolved] = useState<Record<string, ResolvedVariable>>({});

  // When the user changes client OR template, clear all resolved variable
  // state so Step 4 re-runs the resolver against the new client. Without this,
  // values from Client A leak into Client B's session.
  useEffect(() => {
    setVariableValues({});
    setVariableProvenance({});
    setResolved({});
  }, [clientId, templateId]);

  const reset = () => {
    setStep(1);
    setTemplateId(undefined);
    setClientId(undefined);
    setLetterheadId(undefined);
    setVariableValues({});
    setVariableProvenance({});
    setResolved({});
  };

  const totalSteps = 5;
  const progressPct = (step / totalSteps) * 100;

  return (
    <div className="space-y-4">
      <PageHeader
        icon={Sparkles}
        title="Generate Document"
        description={`Step ${step} of ${totalSteps}`}
        actions={
          <Button variant="outline" size="sm" onClick={() => navigate('/panel/automation')}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Automation
          </Button>
        }
      />

      <Progress value={progressPct} className="h-1.5" />

      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <StepBadge num={1} active={step === 1} done={step > 1} label="Template" onClick={() => setStep(1)} />
        <ChevronRight className="h-3 w-3" />
        <StepBadge num={2} active={step === 2} done={step > 2} label="Client" onClick={() => templateId && setStep(2)} disabled={!templateId} />
        <ChevronRight className="h-3 w-3" />
        <StepBadge num={3} active={step === 3} done={step > 3} label="Letterhead" onClick={() => templateId && clientId && setStep(3)} disabled={!clientId} />
        <ChevronRight className="h-3 w-3" />
        <StepBadge num={4} active={step === 4} done={step > 4} label="Variables" onClick={() => templateId && clientId && setStep(4)} disabled={!clientId} />
        <ChevronRight className="h-3 w-3" />
        <StepBadge num={5} active={step === 5} done={false} label="Export" onClick={() => templateId && clientId && Object.keys(resolved).length > 0 && setStep(5)} disabled={Object.keys(resolved).length === 0} />
      </div>

      {step === 1 && (
        <Step1PickTemplate onSelect={(id) => { setTemplateId(id); setStep(2); }} />
      )}
      {step === 2 && templateId && (
        <Step2PickClient
          templateId={templateId}
          onBack={() => urlTemplateId ? navigate('/panel/automation') : setStep(1)}
          onSelect={(id) => { setClientId(id); setStep(3); }}
        />
      )}
      {step === 3 && templateId && clientId && (
        <Step3PickLetterhead
          onBack={() => setStep(2)}
          onSelect={(id) => { setLetterheadId(id); setStep(4); }}
        />
      )}
      {step === 4 && templateId && clientId && (
        <Step4Variables
          templateId={templateId}
          clientId={clientId}
          onBack={() => setStep(3)}
          resolved={resolved}
          setResolved={setResolved}
          variableValues={variableValues}
          setVariableValues={setVariableValues}
          variableProvenance={variableProvenance}
          setVariableProvenance={setVariableProvenance}
          onConfirm={() => setStep(5)}
        />
      )}
      {step === 5 && templateId && clientId && (
        <Step5Preview
          templateId={templateId}
          clientId={clientId}
          letterheadId={letterheadId}
          variableValues={variableValues}
          variableProvenance={variableProvenance}
          onBack={() => setStep(4)}
          onReset={reset}
        />
      )}
    </div>
  );
}

function StepBadge({ num, active, done, label, onClick, disabled }: {
  num: number; active: boolean; done: boolean; label: string;
  onClick?: () => void; disabled?: boolean;
}) {
  const clickable = !!onClick && !disabled && !active;
  return (
    <button
      type="button"
      onClick={() => clickable && onClick?.()}
      disabled={!clickable}
      className={cn(
        'flex items-center gap-1.5 px-2 py-1 rounded-md text-xs transition-colors',
        active ? 'bg-[#5048e5]/10 text-[#5048e5] font-semibold cursor-default' :
        done ? 'text-green-600 hover:bg-green-500/10 cursor-pointer' :
        clickable ? 'text-muted-foreground hover:bg-muted cursor-pointer' :
        'text-muted-foreground/60 cursor-not-allowed'
      )}
    >
      <div className={cn(
        'h-4 w-4 rounded-full flex items-center justify-center text-[10px]',
        active ? 'bg-[#5048e5] text-white' :
        done ? 'bg-green-500 text-white' : 'bg-muted'
      )}>
        {done ? <Check className="h-3 w-3" /> : num}
      </div>
      {label}
    </button>
  );
}

// ==================== Step 1: Pick Template ====================

function Step1PickTemplate({ onSelect }: { onSelect: (id: string) => void }) {
  const navigate = useNavigate();
  const { data: templates = [], isLoading } = useQuery<Template[]>({
    queryKey: ['automation', 'templates'],
    queryFn: async () => {
      try {
        const resp = await apiClient.get('/automation/templates');
        const body = resp.data?.data || resp.data || [];
        return Array.isArray(body) ? body : (body.items || []);
      } catch {
        return [];
      }
    },
  });

  if (isLoading) {
    return <Card><CardContent className="py-6 text-center text-sm text-muted-foreground">Loading templates…</CardContent></Card>;
  }

  if (templates.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-10 flex flex-col items-center gap-3 text-center">
          <FileText className="h-10 w-10 text-muted-foreground" />
          <div>
            <p className="font-medium">No templates yet</p>
            <p className="text-sm text-muted-foreground mt-0.5">
              Upload a Word template with {'{{Variable}}'} and {'{{Anything?}}'} placeholders to get started.
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate('/panel/automation?tab=templates')}>
              <Plus className="mr-2 h-4 w-4" /> Upload Template
            </Button>
            <Button variant="outline" onClick={() => navigate('/panel/automation')}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Automation
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      {templates.map(t => (
        <Card key={t.id} className="cursor-pointer hover:border-[#5048e5]/50 transition-colors" onClick={() => onSelect(t.id)}>
          <CardHeader className="pb-2">
            <div className="flex items-start gap-2">
              <div className="h-9 w-9 rounded bg-blue-500/10 text-blue-500 flex items-center justify-center shrink-0">
                <FileText className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <CardTitle className="text-sm truncate">{t.name}</CardTitle>
                {t.category && <p className="text-[11px] text-muted-foreground mt-0.5">{t.category}</p>}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {t.description && <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{t.description}</p>}
            <Badge variant="secondary" className="text-[10px]">
              {(t.variables?.length ?? 0)} variables
            </Badge>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ==================== Step 2: Pick Client ====================

function Step2PickClient({ templateId, onBack, onSelect }: {
  templateId: string;
  onBack: () => void;
  onSelect: (id: string) => void;
}) {
  const [search, setSearch] = useState('');

  const { data: clients = [], isLoading } = useQuery<Client[]>({
    queryKey: ['automation', 'clients'],
    queryFn: async () => {
      try {
        const resp = await apiClient.get('/clients?limit=500');
        const body = resp.data?.data || resp.data;
        return body?.clients || body?.items || body || [];
      } catch {
        return [];
      }
    },
  });

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return clients;
    return clients.filter(c => {
      const name = (c.displayName || c.display_name || c.name || '').toLowerCase();
      const abn = (c.abn || '').toLowerCase();
      return name.includes(q) || abn.includes(q);
    });
  }, [clients, search]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" /> Pick Client</CardTitle>
        <CardDescription>Choose the client this document is for.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Input placeholder="Search by name or ABN…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <ScrollArea className="h-[420px] border rounded-md">
          {isLoading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Loading clients…</div>
          ) : filtered.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">No clients found.</div>
          ) : (
            <div className="divide-y">
              {filtered.map(c => {
                const name = c.displayName || c.display_name || c.name || 'Unknown';
                const type = c.clientType || c.entity_type;
                return (
                  <button
                    key={c.id}
                    className="w-full flex items-center gap-3 p-3 hover:bg-muted/50 text-left"
                    onClick={() => onSelect(c.id)}
                  >
                    <div className="h-9 w-9 rounded bg-sky-500/10 text-sky-500 flex items-center justify-center shrink-0">
                      <Users className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{name}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {c.abn ? `ABN ${c.abn}` : 'No ABN'} {type ? `· ${type}` : ''}
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
        <div className="flex justify-between">
          <Button variant="outline" onClick={onBack}><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ==================== Step 3: Pick Letterhead ====================

function Step3PickLetterhead({ onBack, onSelect }: {
  onBack: () => void;
  onSelect: (id: string | undefined) => void;
}) {
  const { data: letterheads = [], isLoading } = useQuery<Letterhead[]>({
    queryKey: ['automation', 'letterheads'],
    queryFn: async () => {
      try {
        const resp = await apiClient.get('/automation/letterheads');
        const body = resp.data?.data || resp.data || [];
        return Array.isArray(body) ? body : (body.items || []);
      } catch {
        return [];
      }
    },
  });

  const defaultLh = letterheads.find(l => l.isDefault);
  const [selected, setSelected] = useState<string | 'none'>(defaultLh?.id || 'none');

  useEffect(() => {
    if (defaultLh?.id && selected === 'none') setSelected(defaultLh.id);
  }, [defaultLh?.id]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><Layers className="h-4 w-4" /> Pick Letterhead</CardTitle>
        <CardDescription>Choose the branded letterhead, or skip.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="py-4 text-center text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="space-y-2">
            <button
              className={cn(
                'w-full flex items-center gap-3 p-3 rounded-md border text-left hover:bg-muted/50 transition-colors',
                selected === 'none' && 'border-[#5048e5] bg-[#5048e5]/5'
              )}
              onClick={() => setSelected('none')}
            >
              <div className="h-9 w-9 rounded bg-muted text-muted-foreground flex items-center justify-center">
                <X className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">No letterhead</p>
                <p className="text-[11px] text-muted-foreground">Generate document without branded letterhead.</p>
              </div>
            </button>
            {letterheads.map(lh => (
              <button
                key={lh.id}
                className={cn(
                  'w-full flex items-center gap-3 p-3 rounded-md border text-left hover:bg-muted/50 transition-colors',
                  selected === lh.id && 'border-[#5048e5] bg-[#5048e5]/5'
                )}
                onClick={() => setSelected(lh.id)}
              >
                <div className="h-9 w-9 rounded bg-purple-500/10 text-purple-500 flex items-center justify-center">
                  <Layers className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{lh.name}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {lh.isDefault ? 'Default' : 'Alternate'}
                  </p>
                </div>
                {selected === lh.id && <Check className="h-4 w-4 text-[#5048e5]" />}
              </button>
            ))}
          </div>
        )}
        <div className="flex justify-between pt-2">
          <Button variant="outline" onClick={onBack}><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>
          <Button onClick={() => onSelect(selected === 'none' ? undefined : selected)}>
            Continue <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ==================== Step 4: Variables / Chat ====================

function Step4Variables(props: {
  templateId: string;
  clientId: string;
  onBack: () => void;
  resolved: Record<string, ResolvedVariable>;
  setResolved: React.Dispatch<React.SetStateAction<Record<string, ResolvedVariable>>>;
  variableValues: Record<string, string>;
  setVariableValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  variableProvenance: Record<string, ResolverSource>;
  setVariableProvenance: React.Dispatch<React.SetStateAction<Record<string, ResolverSource>>>;
  onConfirm: () => void;
}) {
  const { templateId, clientId, onBack, resolved, setResolved, variableValues, setVariableValues,
    variableProvenance, setVariableProvenance, onConfirm } = props;

  const resolveMutation = useMutation({
    mutationFn: async () => {
      const resp = await apiClient.post(`/automation/templates/${templateId}/resolve`, { clientId });
      return resp.data?.data || resp.data;
    },
    onSuccess: (data: any) => {
      const vars: Record<string, ResolvedVariable> = data?.variables || {};
      setResolved(vars);
      const vals: Record<string, string> = {};
      const prov: Record<string, ResolverSource> = {};
      Object.entries(vars).forEach(([name, v]) => {
        vals[name] = v.value ?? '';
        prov[name] = v.source;
      });
      setVariableValues(vals);
      setVariableProvenance(prov);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to resolve variables');
    },
  });

  // Run resolve on entry
  useEffect(() => {
    if (Object.keys(resolved).length === 0) {
      resolveMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const autoVars = useMemo(
    () => Object.values(resolved).filter(v => !v.isUserPrompted && v.source !== 'unresolved'),
    [resolved]
  );

  const pendingVars = useMemo(
    () => Object.values(resolved).filter(v => v.isUserPrompted || v.source === 'unresolved'),
    [resolved]
  );

  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [searchingVar, setSearchingVar] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Convert an UPPER_CASE / snake_case variable name into a friendly phrase
  // for the chat question. e.g. "Owner_Name" → "owner name", "ABN" → "ABN".
  const humaniseVarName = (n: string) => n.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim();

  /**
   * Ask the chat for a value instead of running a structured extract.
   * The conversational LLM with full document + Xero + facts context tends
   * to be much more accurate than the standalone extractor — it can reason
   * about why a director or company name is NOT what the user is asking for.
   * The user reads the answer and copies it into the variable input.
   */
  const askInChat = (varName: string) => {
    if (chatBusy) return;
    const phrase = humaniseVarName(varName);
    const question = `For variable {{${varName}}} (which means the ${phrase}), what is the correct value for this client based on the documents? Quote it exactly so I can paste it into the template. If you find more than one candidate, list them with a short note on which is most likely correct.`;
    // Briefly highlight which var we're asking about so the user knows where
    // to paste the answer.
    setSearchingVar(varName);
    sendChat(question).finally(() => {
      setSearchingVar(null);
    });
  };

  const remainingVars = useMemo(
    () => pendingVars.filter(v => !((variableValues[v.name] ?? '').trim().length > 0)),
    [pendingVars, variableValues]
  );

  // Greet when vars are loaded
  useEffect(() => {
    if (!resolveMutation.isSuccess) return;
    if (chatMessages.length > 0) return;
    const greeting = pendingVars.length === 0
      ? `I've auto-filled all ${autoVars.length} variables from this client's record. Everything is ready — review on the left and click Generate.`
      : `I've auto-filled ${autoVars.length} variable${autoVars.length === 1 ? '' : 's'} from this client's record. I still need: ${pendingVars.map(v => `**${v.name}**`).join(', ')}.\n\nYou can fill them on the left, or ask me anything about this client (documents, Xero, owner details, etc.) and I'll help you find the values.`;
    setChatMessages([{ id: 'greet', role: 'assistant', content: greeting }]);
    if (pendingVars.length === 0) setShowSummary(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolveMutation.isSuccess]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [chatMessages, chatBusy]);

  const sendChat = async (forcedMessage?: string) => {
    const trimmed = (forcedMessage ?? chatInput).trim();
    if (!trimmed || chatBusy) return;

    const userMsgId = `u-${Date.now()}`;
    setChatMessages(prev => [...prev, { id: userMsgId, role: 'user', content: trimmed }]);
    if (!forcedMessage) setChatInput('');
    setChatBusy(true);

    // Build a short variable hint so the model knows the document context.
    const remainingHint = remainingVars.length > 0
      ? `\n\n[Context — variables still needed for the document being generated: ${remainingVars.map(v => v.name).join(', ')}. If the user is providing one of these values, acknowledge briefly and they'll set it on the left panel.]`
      : '';
    const messageWithContext = trimmed + remainingHint;

    try {
      const history = chatMessages.map(m => ({ role: m.role, content: m.content }));
      const resp = await apiClient.post(`/clients/${clientId}/chat`, {
        clientId,
        message: messageWithContext,
        conversationHistory: history,
        dataSource: 'both',
      });
      const data = resp.data?.data || resp.data;
      const answer = data?.message?.content || data?.answer || 'No response.';
      const citations = data?.message?.citations || data?.sources || [];
      setChatMessages(prev => [...prev, {
        id: `a-${Date.now()}`,
        role: 'assistant',
        content: answer,
        citations: citations.map((c: any) => ({
          documentName: c.documentName,
          excerpt: c.excerpt,
          sourceUrl: c.sourceUrl,
        })),
      }]);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Chat failed.';
      setChatMessages(prev => [...prev, {
        id: `e-${Date.now()}`,
        role: 'assistant',
        content: `Sorry — ${msg}`,
      }]);
    } finally {
      setChatBusy(false);
    }
  };

  // Active as soon as every pending variable has a non-empty value, regardless
  // of whether the user answered via chat or via inline-edit.
  const allFilled = pendingVars.every(v => ((variableValues[v.name] ?? '').trim().length > 0));
  const allDone = pendingVars.length === 0 || allFilled;

  // Auto-flip the chat into "all collected" UI once every pending var has a value.
  // NOTE: Must live ABOVE the early returns — otherwise the hook is called on some
  // renders but not others, which breaks the Rules of Hooks (React error #300).
  useEffect(() => {
    if (allFilled && !showSummary) setShowSummary(true);
  }, [allFilled, showSummary]);

  if (resolveMutation.isPending) {
    return (
      <Card><CardContent className="py-10 text-center">
        <Loader2 className="h-6 w-6 mx-auto mb-2 animate-spin text-[#5048e5]" />
        <p className="text-sm text-muted-foreground">Resolving variables from client record and Xero…</p>
      </CardContent></Card>
    );
  }

  if (resolveMutation.isError) {
    return (
      <Card><CardContent className="py-10 text-center space-y-3">
        <p className="text-sm text-destructive">Could not resolve variables.</p>
        <Button onClick={() => resolveMutation.mutate()} size="sm">Retry</Button>
      </CardContent></Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-4">
      {/* Left: Variable summary */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" /> Variables</CardTitle>
          <CardDescription>{Object.keys(resolved).length} variables · {autoVars.length} auto · {pendingVars.length} user input</CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px] pr-2">
            <div className="space-y-2">
              {Object.values(resolved).map(v => {
                const currentValue = variableValues[v.name] ?? v.value ?? '';
                const currentSource = (variableProvenance[v.name] || v.source) as ResolverSource;
                // A variable is "searchable" if the backend flagged it (either
                // a known user-prompted var or an unresolved one). We also offer
                // search for any currently-empty field — the AI may find a value
                // even when the hardcoded resolver didn't match.
                const isSearchable = !!v.searchable || !currentValue.trim();
                const isSearching = searchingVar === v.name;
                const onFieldChange = (newVal: string) => {
                  setVariableValues(prev => ({ ...prev, [v.name]: newVal }));
                  setVariableProvenance(prev => ({ ...prev, [v.name]: 'user_input' }));
                  setResolved(prev => ({
                    ...prev,
                    [v.name]: { ...prev[v.name], value: newVal, source: 'user_input' },
                  }));
                };
                return (
                  <div key={v.name} className="rounded-md border p-2">
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1 space-y-1">
                        <p className="font-mono text-[11px] text-muted-foreground truncate">{`{{${v.name}}}`}</p>
                        <div className="flex items-center gap-1">
                          <Input
                            className="h-8 text-sm flex-1"
                            value={currentValue}
                            placeholder="— empty —"
                            onChange={(e) => onFieldChange(e.target.value)}
                          />
                          {isSearchable && (
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 shrink-0"
                              title="Ask the chat to find this value in the client's documents (then copy + paste the answer)"
                              disabled={isSearching || chatBusy}
                              onClick={() => askInChat(v.name)}
                            >
                              {isSearching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
                            </Button>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-wrap">
                          <Badge variant="outline" className={cn('text-[10px] border', SOURCE_COLORS[currentSource])}>
                            {SOURCE_LABELS[currentSource]}
                          </Badge>
                          {v.sourceDetail && <span className="text-[10px] text-muted-foreground truncate">{v.sourceDetail}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          <div className="flex justify-between mt-3 pt-3 border-t">
            <Button variant="outline" onClick={onBack}><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>
            <Button onClick={onConfirm} disabled={!allDone}>
              Looks right, generate <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Right: Chat — full conversational chat with the client's data */}
      <Card className="flex flex-col">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Client Chat</CardTitle>
          <CardDescription>Ask anything about this client's documents, Xero data, or details.</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col gap-2">
          {remainingVars.length > 0 && (
            <div className="rounded-md bg-amber-500/10 border border-amber-500/30 p-2 text-xs text-amber-800 flex items-start gap-2">
              <Pencil className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold">Still needed:</p>
                <p className="font-mono mt-0.5">{remainingVars.map(v => `{{${v.name}}}`).join(', ')}</p>
                <p className="text-amber-700 mt-1">Fill them on the left panel, or ask me here for help finding the values.</p>
              </div>
            </div>
          )}
          <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-2 min-h-[340px] max-h-[420px] border rounded-md p-3 bg-muted/20">
            {chatMessages.map(msg => (
              <div key={msg.id} className={cn('flex', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
                <div className={cn(
                  'max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap',
                  msg.role === 'user' ? 'bg-[#5048e5] text-white' :
                  msg.role === 'system' ? 'bg-muted text-muted-foreground italic' :
                  'bg-background border'
                )}>
                  {msg.content.split('**').map((part, i) =>
                    i % 2 === 1 ? <strong key={i} className="font-mono text-xs">{part}</strong> : <span key={i}>{part}</span>
                  )}
                  {msg.citations && msg.citations.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-current/10 space-y-1">
                      {msg.citations.slice(0, 3).map((c, i) => (
                        <p key={i} className="text-[10px] text-muted-foreground italic truncate">
                          {c.documentName ? `📄 ${c.documentName}` : c.sourceUrl || ''}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {chatBusy && (
              <div className="flex justify-start">
                <div className="bg-background border rounded-lg px-3 py-2 text-sm flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Input
              placeholder={remainingVars.length > 0
                ? `Ask anything, or type a value for ${remainingVars[0].name}…`
                : 'Ask anything about this client…'}
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !chatBusy) sendChat(); }}
              disabled={chatBusy}
              autoFocus
            />
            <Button onClick={sendChat} disabled={!chatInput.trim() || chatBusy}><Send className="h-4 w-4" /></Button>
          </div>
          {showSummary && (
            <div className="rounded-md bg-green-500/10 border border-green-500/30 p-2 text-xs text-green-700 flex items-center gap-2">
              <Check className="h-3.5 w-3.5" /> All variables collected — ready to generate.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== Step 5: Preview & Export ====================

function Step5Preview(props: {
  templateId: string;
  clientId: string;
  letterheadId?: string;
  variableValues: Record<string, string>;
  variableProvenance: Record<string, ResolverSource>;
  onBack: () => void;
  onReset: () => void;
}) {
  const { templateId, clientId, letterheadId, variableValues, variableProvenance, onBack, onReset } = props;
  const [phase, setPhase] = useState<'generating-docx' | 'generating-pdf' | 'ready' | 'error'>('generating-docx');
  const [result, setResult] = useState<{
    id: string; docxUrl: string; pdfUrl: string | null; pdfPreviewUrl: string | null;
    filename: string; pdfStatus?: string; pdfError?: string;
  } | null>(null);
  const [iframeFailed, setIframeFailed] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const hasRunRef = useRef(false);

  const { data: client } = useQuery<Client>({
    queryKey: ['automation', 'client', clientId],
    queryFn: async () => {
      try {
        const resp = await apiClient.get(`/clients/${clientId}`);
        return resp.data?.data || resp.data;
      } catch {
        return {} as Client;
      }
    },
  });

  // On-demand PDF generation — used when the inline auto-conversion failed
  // (e.g. LibreOffice layer compatibility) and the user wants to retry.
  const generatePdfMutation = useMutation({
    mutationFn: async () => {
      if (!result?.id) throw new Error('No generated document yet');
      const resp = await apiClient.post(`/automation/generated/${result.id}/generate-pdf`);
      return resp.data?.data || resp.data;
    },
    onSuccess: (data: any) => {
      setResult(prev => prev ? {
        ...prev,
        pdfUrl: data.pdfUrl ?? prev.pdfUrl,
        pdfPreviewUrl: data.pdfPreviewUrl ?? prev.pdfPreviewUrl,
        pdfStatus: 'ready',
        pdfError: undefined,
      } : prev);
      setIframeFailed(false);
      setIframeLoaded(false);
      toast.success('PDF generated successfully');
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error || err?.message || 'PDF generation failed';
      toast.error(msg);
      setResult(prev => prev ? { ...prev, pdfError: msg } : prev);
    },
  });

  const generateMutation = useMutation({
    mutationFn: async () => {
      setPhase('generating-docx');
      const resp = await apiClient.post(`/automation/templates/${templateId}/generate`, {
        clientId,
        letterheadId,
        variableValues,
        variableProvenance,
      });
      return resp.data?.data || resp.data;
    },
    onSuccess: (data: any) => {
      setPhase('generating-pdf');
      setResult({
        id: data.id,
        docxUrl: data.docxUrl,
        pdfUrl: data.pdfUrl ?? null,
        pdfPreviewUrl: data.pdfPreviewUrl ?? null,
        filename: data.filename || 'document.pdf',
        pdfStatus: data.pdfStatus,
        pdfError: data.pdfError,
      });
      setTimeout(() => setPhase('ready'), 400);
    },
    onError: (err: any) => {
      setPhase('error');
      toast.error(err?.response?.data?.message || 'Failed to generate document');
    },
  });

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;
    generateMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fallback if iframe doesn't load in 15s
  useEffect(() => {
    if (phase !== 'ready' || !result?.pdfPreviewUrl) return;
    const timer = setTimeout(() => {
      if (!iframeLoaded) setIframeFailed(true);
    }, 15000);
    return () => clearTimeout(timer);
  }, [phase, result, iframeLoaded]);

  if (phase === 'generating-docx' || phase === 'generating-pdf') {
    return (
      <Card><CardContent className="py-10 text-center space-y-3">
        <Loader2 className="h-8 w-8 mx-auto animate-spin text-[#5048e5]" />
        <p className="text-sm font-medium">{phase === 'generating-docx' ? 'Generating .docx…' : 'Converting to PDF…'}</p>
        <p className="text-xs text-muted-foreground">This may take a few seconds.</p>
      </CardContent></Card>
    );
  }

  if (phase === 'error') {
    return (
      <Card><CardContent className="py-10 text-center space-y-3">
        <p className="text-sm text-destructive">Failed to generate document.</p>
        <div className="flex gap-2 justify-center">
          <Button variant="outline" onClick={onBack}>Back</Button>
          <Button onClick={() => { hasRunRef.current = false; generateMutation.mutate(); }}>Retry</Button>
        </div>
      </CardContent></Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Preview</CardTitle>
          <CardDescription>{result?.filename}</CardDescription>
        </CardHeader>
        <CardContent className="p-2">
          {result?.pdfPreviewUrl && !iframeFailed ? (
            <iframe
              src={result.pdfPreviewUrl}
              title="Document preview"
              className="w-full h-[640px] border rounded-md bg-muted/20"
              onLoad={() => setIframeLoaded(true)}
              onError={() => setIframeFailed(true)}
            />
          ) : result?.docxUrl ? (
            <div className="h-[640px] flex flex-col items-center justify-center gap-3 border rounded-md bg-muted/20 px-6">
              <FileText className="h-10 w-10 text-muted-foreground" />
              <p className="text-sm font-medium">
                {result.pdfStatus === 'pending'
                  ? 'Word document is ready — generate PDF on demand'
                  : 'Click to open the document.'}
              </p>
              <p className="text-xs text-muted-foreground max-w-md text-center">
                The Word version preserves all formatting. Click <strong>Generate PDF</strong> below to convert it — the PDF is created from the Word file (not regenerated), so layout, fonts and tables stay identical.
              </p>
              {result.pdfError && (
                <p className="text-xs text-amber-700 bg-amber-500/10 border border-amber-500/30 rounded-md px-2 py-1 max-w-md text-center">
                  Last attempt: {result.pdfError}
                </p>
              )}
              <div className="flex flex-wrap gap-2 justify-center">
                {!result.pdfUrl && (
                  <Button
                    onClick={() => generatePdfMutation.mutate()}
                    disabled={generatePdfMutation.isPending}
                  >
                    {generatePdfMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Converting Word → PDF…
                      </>
                    ) : (
                      <>
                        <Sparkles className="mr-2 h-4 w-4" />
                        Generate PDF
                      </>
                    )}
                  </Button>
                )}
                {result.pdfUrl && (
                  <Button onClick={() => window.open(result.pdfUrl!, '_blank')}>
                    <Download className="mr-2 h-4 w-4" /> Download PDF
                  </Button>
                )}
                <Button variant="outline" onClick={() => window.open(result.docxUrl, '_blank')}>
                  <Download className="mr-2 h-4 w-4" /> Download Word
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Export</CardTitle>
          <CardDescription>Download or email the document.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Button className="w-full" variant="outline" disabled={!result?.docxUrl} onClick={() => result?.docxUrl && window.open(result.docxUrl, '_blank')}>
            <Download className="mr-2 h-4 w-4" /> Download Word
          </Button>
          <Button className="w-full" variant="outline" disabled={!result?.pdfUrl} onClick={() => result?.pdfUrl && window.open(result.pdfUrl, '_blank')}>
            <Download className="mr-2 h-4 w-4" /> {result?.pdfUrl ? 'Download PDF' : 'PDF pending…'}
          </Button>
          <Button className="w-full" onClick={() => setEmailOpen(true)} disabled={!result}>
            <Send className="mr-2 h-4 w-4" /> Send to Client
          </Button>
          <div className="pt-3 border-t space-y-2">
            <Button className="w-full" variant="ghost" onClick={onBack}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Variables
            </Button>
            <Button className="w-full" variant="secondary" onClick={onReset}>
              <Plus className="mr-2 h-4 w-4" /> Start New
            </Button>
          </div>
        </CardContent>
      </Card>

      <SendEmailDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        generatedId={result?.id || null}
        filename={result?.filename}
        defaultTo={client?.email}
      />
    </div>
  );
}
